import React, { useCallback, useId, useState } from 'react';
import Button from '../common/Button';
import RegisterModal from './RegisterModal';
import PasswordLoginModal from './PasswordLoginModal';
import ForgotPasswordModal from './ForgotPasswordModal';
import config from '../../config/environment';
import './LoginForm.css';

interface LoginFormProps {
  /** Full-page redirect after password sign-in. Omit it to let the sign-in route guard decide. */
  redirectTo?: string;
  onPasswordSuccess?: (response: any) => void;
}

/** Campus SSO is the primary route; registered accounts use password sign-in. */
const LoginForm: React.FC<LoginFormProps> = ({ redirectTo, onPasswordSuccess }) => {
  const [isRegisterModalOpen, setIsRegisterModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isForgotPasswordModalOpen, setIsForgotPasswordModalOpen] = useState(false);
  const routeId = useId();
  const closePasswordModal = useCallback(() => setIsPasswordModalOpen(false), []);

  const handleUIUCSSOLogin = (event: React.FormEvent) => {
    event.preventDefault();
    window.location.href = `${config.API_BASE_URL}/oauth2/start?rd=/auth/sso/login`;
  };

  return (
    <div className="login-form">
      <div className="login-form__header">
        <h2 className="login-form__title">Sign in</h2>
      </div>

      <form className="login-form__form" onSubmit={handleUIUCSSOLogin}>
        <div className="login-form__route">
          <h3 className="login-form__route-title" id={`${routeId}-illinois`}>Illinois (UIUC)</h3>
          <Button
            type="submit"
            variant="primary"
            size="lg"
            className="login-form__submit-button login-form__submit-button--sso"
            aria-describedby={`${routeId}-illinois`}
          >
            Sign in with Illinois
          </Button>
        </div>
      </form>

      <div className="login-form__route login-form__route--other">
        <h3 className="login-form__route-title" id={`${routeId}-password`}>Other accounts</h3>
        <Button
          type="button"
          variant="outline"
          size="md"
          className="login-form__password-button"
          onClick={() => setIsPasswordModalOpen(true)}
          aria-haspopup="dialog"
          aria-describedby={`${routeId}-password`}
        >
          Sign in with password
        </Button>
      </div>

      {config.FEATURES.REGISTRATION_ENABLED && (
        <div className="login-form__footer">
          <p className="login-form__footer-text">
            Need an iKompete account?{' '}
            <button type="button" className="login-form__register-link" onClick={() => setIsRegisterModalOpen(true)}>
              Create an account
            </button>
          </p>
        </div>
      )}

      <RegisterModal
        isOpen={isRegisterModalOpen}
        onClose={() => setIsRegisterModalOpen(false)}
        onSuccess={() => setIsRegisterModalOpen(false)}
      />
      <PasswordLoginModal
        isOpen={isPasswordModalOpen}
        onClose={closePasswordModal}
        redirectTo={redirectTo}
        onSuccess={onPasswordSuccess}
        onForgotPassword={() => {
          setIsPasswordModalOpen(false);
          setIsForgotPasswordModalOpen(true);
        }}
      />
      <ForgotPasswordModal
        isOpen={isForgotPasswordModalOpen}
        onClose={() => setIsForgotPasswordModalOpen(false)}
      />
    </div>
  );
};

export default LoginForm;
